import React from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemIcon, ListItemText, Alert, Button, Link } from '@mui/material';
import {
  Facebook as FacebookIcon,
  Google as GoogleIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { SupportLayout } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const DataDeletionPage: React.FC = () => {
  useDocumentTitle('Data Deletion - Support');

  return (
    <SupportLayout
      title="Data Deletion & Privacy"
      description="Learn how to manage your data, disconnect third-party accounts, and request deletion of your SquareUp account."
      breadcrumbs={[{ label: 'Data Deletion' }]}
    >
      {/* Overview */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Your Privacy Matters
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          At SquareUp, we respect your privacy and give you full control over your data. You can disconnect
          third-party login providers, manage your account information, or request complete deletion of your
          account and all associated data.
        </Typography>
      </Paper>

      {/* What data we collect */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
        What Data We Collect
      </Typography>
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider' }}>
        <Typography variant="body1" paragraph>
          When you sign up or log in using a third-party provider (like Facebook or Google), we collect:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Email address"
              secondary="Used for account identification and important notifications"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Name (first and last)"
              secondary="Used to personalize your experience and display on boards"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Provider user ID"
              secondary="A unique identifier used to link your SquareUp account to your social login"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="primary" fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Profile picture URL (if available)"
              secondary="May be used to display your avatar"
            />
          </ListItem>
        </List>
        <Alert severity="info" sx={{ mt: 2 }}>
          We do not access your friends list, posts, messages, or any other private content from your social media accounts.
        </Alert>
      </Paper>

      {/* Disconnect Facebook */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
        Disconnect Facebook
      </Typography>
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FacebookIcon sx={{ color: '#1877F2' }} />
          <Typography variant="h6">
            Remove Facebook Connection
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          To disconnect your Facebook account from SquareUp:
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 'bold' }}>1</Box>
            </ListItemIcon>
            <ListItemText
              primary="Go to Profile Settings"
              secondary="Click on your profile icon and select 'Settings'"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 'bold' }}>2</Box>
            </ListItemIcon>
            <ListItemText
              primary="Find Connected Accounts"
              secondary="Scroll down to the 'Connected Accounts' section"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 'bold' }}>3</Box>
            </ListItemIcon>
            <ListItemText
              primary="Click Unlink"
              secondary="Click the 'Unlink' button next to Facebook"
            />
          </ListItem>
        </List>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Note:</strong> You must have a password set or another connected account (like Google) before you can disconnect Facebook.
          This ensures you can still access your account.
        </Alert>
        <Box sx={{ mt: 3 }}>
          <Button
            component={RouterLink}
            to="/profile"
            variant="contained"
            startIcon={<SettingsIcon />}
          >
            Go to Profile Settings
          </Button>
        </Box>
      </Paper>

      {/* Disconnect Google */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
        Disconnect Google
      </Typography>
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GoogleIcon sx={{ color: '#DB4437' }} />
          <Typography variant="h6">
            Remove Google Connection
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          To disconnect your Google account from SquareUp, follow the same steps as above:
          go to Profile Settings, find Connected Accounts, and click Unlink next to Google.
        </Typography>
        <Alert severity="warning" sx={{ mt: 2 }}>
          <strong>Note:</strong> You must have a password set or another connected account (like Facebook) before you can disconnect Google.
        </Alert>
      </Paper>

      {/* Delete Account */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
        Delete Your Account
      </Typography>
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'error.light', bgcolor: 'error.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" color="error.main">
            Request Account Deletion
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          If you want to completely delete your SquareUp account and all associated data, please contact us
          at the email address below. We will process your request within 30 days.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>What gets deleted:</strong>
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><DeleteIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your account profile and login credentials" />
          </ListItem>
          <ListItem>
            <ListItemIcon><DeleteIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="All boards you created (if not shared with others)" />
          </ListItem>
          <ListItem>
            <ListItemIcon><DeleteIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Your participation history on boards" />
          </ListItem>
          <ListItem>
            <ListItemIcon><DeleteIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Connected social account information" />
          </ListItem>
          <ListItem>
            <ListItemIcon><DeleteIcon color="error" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Payment and transaction history" />
          </ListItem>
        </List>
        <Alert severity="error" sx={{ mt: 2, mb: 3 }}>
          <strong>Warning:</strong> Account deletion is permanent and cannot be undone. Any unused credits will be forfeited.
        </Alert>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmailIcon color="primary" />
          <Typography variant="body1">
            To request account deletion, email us at:{' '}
            <Link href="mailto:support@katapultmedia.com?subject=SquareUp%20Account%20Deletion%20Request" underline="hover">
              <strong>support@katapultmedia.com</strong>
            </Link>
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Please include "Account Deletion Request" in your subject line and send from the email address
          associated with your SquareUp account.
        </Typography>
      </Paper>

      {/* Data Retention */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
        Data Retention
      </Typography>
      <Paper elevation={0} sx={{ p: 3, mb: 4, border: 1, borderColor: 'divider' }}>
        <Typography variant="body1" paragraph>
          When you disconnect a social login provider (Facebook, Google), we immediately remove the
          connection between your SquareUp account and that provider. However, your SquareUp account
          remains active with all your boards, history, and credits intact.
        </Typography>
        <Typography variant="body1" paragraph>
          When you request full account deletion, we will:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Delete all your personal information within 30 days" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Remove your participation from active boards" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Delete draft boards you created" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
            <ListItemText primary="Anonymize your data in completed boards (for record-keeping purposes)" />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Some anonymized data may be retained for legal, tax, or fraud prevention purposes as required by law.
        </Typography>
      </Paper>

      {/* More Information */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mt: 4,
          textAlign: 'center',
          bgcolor: 'grey.50',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Questions about your data?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          For more information about how we handle your data, please review our Privacy Policy or contact us.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Button
            component={RouterLink}
            to="/privacy"
            variant="outlined"
          >
            Privacy Policy
          </Button>
          <Button
            component="a"
            href="mailto:support@katapultmedia.com"
            variant="outlined"
          >
            Contact Support
          </Button>
        </Box>
      </Paper>
    </SupportLayout>
  );
};

export default DataDeletionPage;
